import { useEffect, useCallback, useMemo, FC } from 'react';
import intl from 'react-intl-universal';
import debounce from 'lodash/debounce';
import { FormItem, UsersPicker } from '@getsynapse/design-system';
import { FilterParams } from 'types/store/projectOwners';
import { AvatarUser } from 'utils/customTypes';
import useProjectOwners from 'Pages/ProjectsListPage/hooks/useProjectOwners/useProjectOwners';

interface ProjectOwnersFilterProps {
  onSelect: (selected: string[]) => void;
  selected?: string[];
}

export interface ProjectOwnerOption {
  value: string;
  label: string;
  disabled: boolean;
  avatar: AvatarUser;
}

const ProjectOwnersFilter: FC<ProjectOwnersFilterProps> = ({
  onSelect,
  selected,
}) => {
  const {
    fetchAvailableProjectOwners,
    loadMoreProjectOwners,
    canGetMoreProjectOwners,
    availableProjectOwners: projectOwners,
    handleFilterProjectOwners,
    resetProjectOwnersState,
  } = useProjectOwners();

  useEffect(() => {
    fetchAvailableProjectOwners();
    return () => {
      resetProjectOwnersState();
    };
  }, [fetchAvailableProjectOwners, resetProjectOwnersState]);

  const onFilterChange = useCallback(
    (filter: string) => {
      const ownerName = filter.trim().split(' ');
      const filterParams: FilterParams = {
        firstName: ownerName[0],
      };
      if (ownerName.length > 1) {
        filterParams.lastName = ownerName[1];
      }
      handleFilterProjectOwners(filterParams);
    },
    [handleFilterProjectOwners]
  );

  const debouncedFilterChange = useMemo(() => {
    return debounce((filter: string) => onFilterChange(filter), 500);
  }, [onFilterChange]);

  // Cleanup debounced function to prevent execution after component unmount
  useEffect(() => {
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [debouncedFilterChange]);

  const handleOnChange = useCallback(
    (selected: ProjectOwnerOption[]) => {
      const selectedIds = selected.map(
        (user: ProjectOwnerOption) => user.value
      );
      onSelect(selectedIds);
    },
    [onSelect]
  );

  const selectedOwners = useMemo(() => {
    return projectOwners.filter((owner) => selected?.includes(owner.value));
  }, [selected, projectOwners]);

  return (
    <FormItem label={intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.PROJECT_OWNER')}>
      <UsersPicker
        deactivatedUserText={intl.get('DEACTIVATED')}
        usersList={projectOwners}
        selectedUsersList={selectedOwners}
        onChange={handleOnChange}
        enableLazyLoading={canGetMoreProjectOwners}
        loadMoreOptions={loadMoreProjectOwners}
        onFilterChange={debouncedFilterChange}
        triggerProps={{
          'aria-label': 'project-owners-filter',
        }}
        triggerText={intl.get('FILTER_GENERAL.PLACEHOLDER')}
      />
    </FormItem>
  );
};

export default ProjectOwnersFilter;
